import { HttpClient } from '@angular/common/http';
import {share} from 'rxjs/operators';
import {Injectable, Injector, OnInit} from '@angular/core';
import {getMessageFromException} from '../utilities/Util';
import {
    Client,
    ClientClient,
    ClientRevealSsnParams,
    ClientSearchTypes,
    MergeClientParameters,
    PagedListOfClient,
    SimpleBooleanResult, SimpleStringResult
} from './api.client';
import {NotificationService} from './NotificationService';
import {BehaviorSubject, Observable} from 'rxjs';
import {distinctUntilChanged, map} from 'rxjs/operators';
import {StateResetRegistry} from './state-reset-registry.service';
// api
// models

// 003-retire-ngrx (US2): the client slice moved off NgRx into this service.
// currentClient genuinely flows across routes (search -> detail -> manage,
// resolver guards) so it stays shared here. The old isClientsLoading flag had
// no selector (written, never read) and was not ported. The paged search
// results are NOT held here: their only consumer is ClientListComponent,
// whose search criteria live on the component — service-held results outlived
// the criteria that produced them, breaking pagination after back-navigation.
interface ClientSliceState {
    currentClient: Client;
}

const initialState: ClientSliceState = {
    currentClient: null
};

@Injectable()
export class ClientService {

    currentClient: Client;

    private readonly _state$ = new BehaviorSubject<ClientSliceState>(initialState);

    // ---------------- Streams (former client selectors) ----------------
    readonly currentClient$: Observable<Client> = this.select((s) => s.currentClient);

    get isClientVerified(): boolean {
        // "Verified" now means the client's data was confirmed recently: the backend
        // computes isVerificationStale from Client.LastVerifiedDate against a configured
        // window. A never-verified or stale client is not verified. (No currentClient
        // yet — the service is read from guards — counts as not verified.)
        return !!this.currentClient && this.currentClient.isVerificationStale === false;
    }

    constructor(private _http: HttpClient,
                private _clientApiService: ClientClient,
                private _notificationService: NotificationService,
                private injector: Injector,
                resetRegistry: StateResetRegistry) {
        this.currentClient$.subscribe(currentClient => this.currentClient = currentClient);
        resetRegistry.register(() => this._state$.next(initialState));
    }

    // Stamp the client as verified-now (Client.LastVerifiedDate) via the backend and
    // refresh the current client from the returned DTO (which carries the recomputed
    // isVerificationStale). Advisory only — nothing blocks on verification.
    verifyClient(id: string): Observable<Client> {
        const request = this._clientApiService.verifyClient(id).pipe(share());
        request.subscribe({
            next: (client) => {
                this.setCurrentClientAction(client);
                this._notificationService.showSuccess('Client verified.');
            }, error: (err) => {
                this._notificationService.showError(getMessageFromException(err));
            }
        });
        return request;
    }

    getClients(searchType: ClientSearchTypes,
               searchValue: string,
               agencyId: string,
               sortName: string,
               descending: boolean,
               limit: number,
               offset: number,
               secondarySortName?: string): Observable<PagedListOfClient> {
        const request = this._clientApiService.searchClients(
            searchType,
            searchValue,
            agencyId,
            sortName,
            secondarySortName,
            descending,
            limit || 10,
            offset || 0
        ).pipe(share());
        request.subscribe({
            error: (err) => {
                this._notificationService.showError(getMessageFromException(err));
            }
        });
        return request;
    }

    getClientById(id: string): Observable<Client | null> {
        const request = this._clientApiService.retrieveClient(id).pipe(share());
        request.subscribe((result) => {
            this.setCurrentClientAction(result);
        });
        return request;
    }

    clientUniqueSsnCheck(ssn: string, id: string): Observable<SimpleBooleanResult> {
        return this._clientApiService.clientUniqueSsnCheck(ssn, id).pipe(share());
    }


    setCurrentClient(currentClient: Client): void {
        this.setCurrentClientAction(currentClient);
    }

    addClient(client: Client): Observable<Client> {
        const request = this._clientApiService.addClient(client).pipe(share());
        this._notificationService.showSuccess('Attempting to save new Client.');
        request.subscribe({
            next: (result) => {
                this.setCurrentClientAction(result);
                this._notificationService.showSuccess('Client Successfully Created!');
            }, error: (err) => {
                this._notificationService.showError(getMessageFromException(err));
            }, complete: () => {

            }
        });
        return request;
    }

    updateClient(client: Client): Observable<Client> {
        const request = this._clientApiService.updateClient(client).pipe(share());
        this._notificationService.showSuccess('Attempting to update Client.');
        request.subscribe({
            next: (result) => {
                this.setCurrentClientAction(result);
                this._notificationService.showSuccess('Client Successfully Updated!');
            }, error: (err) => {
                this._notificationService.showError(getMessageFromException(err));
            }, complete: () => {

            }
        });
        return request;
    }

    mergeClient(srcClientId, destClientId): Observable<Client> {
        const mergeClientParameters = new MergeClientParameters({
            srcClientId,
            destClientId
        });
        return this._clientApiService.mergeClients(mergeClientParameters);
    }

    exposeClientSsn(clientId: string, password: string): Observable<SimpleStringResult> {
        const revealParams = ClientRevealSsnParams.fromJS({clientId, enteredPassword: password});
        const request = this._clientApiService.exposeSsn(revealParams).pipe(share());
        request.subscribe({
            next: () => {

            }, error: () => {
                this._notificationService.showError('There was an error getting the SSN.');
            }
        });
        return request;
    }

    // ---------------- State transitions (former client actions) ------------------
    setCurrentClientAction(currentClient: Client): void {
        this._state$.next({ ...this._state$.value, currentClient });
    }

    private select<T>(project: (state: ClientSliceState) => T): Observable<T> {
        return this._state$.pipe(map(project), distinctUntilChanged());
    }
}
